import { useRef, useState, useEffect } from "react";
import { useOnClickOutside } from "usehooks-ts";
import "./NumberRangeFilter.css";

interface NumberRangeValue {
	min?: number;
	max?: number;
}

interface NumberRangeFilterProps {
	/** Placeholder text when no value is set */
	placeholder?: string;
	/** Current value */
	value?: NumberRangeValue;
	/** Callback when value changes */
	onChange: (value: NumberRangeValue | undefined) => void;
	/** Minimum allowed value (optional) */
	minAllowed?: number;
	/** Maximum allowed value (optional) */
	maxAllowed?: number;
	/** Step increment for inputs (default: 1) */
	step?: number;
}

/**
 * NumberRangeFilter - A modal-like component for filtering by numeric range.
 * Similar to DateTimeRangeCalendar but for numbers.
 */
export function NumberRangeFilter({
	placeholder = "Select range...",
	value,
	onChange,
	minAllowed,
	maxAllowed,
	step = 1,
}: NumberRangeFilterProps) {
	const containerRef = useRef<HTMLDivElement>(null);
	const [show, setShow] = useState(false);
	const [localMin, setLocalMin] = useState<string>("");
	const [localMax, setLocalMax] = useState<string>("");

	// Sync local state with prop value
	useEffect(() => {
		setLocalMin(value?.min !== undefined ? String(value.min) : "");
		setLocalMax(value?.max !== undefined ? String(value.max) : "");
	}, [value?.min, value?.max]);

	// Close on click outside
	useOnClickOutside(containerRef, () => {
		setShow(false);
	});

	// Format display value for the input field
	const getDisplayValue = (): string => {
		if (!value) return "";
		const { min, max } = value;
		if (min !== undefined && max !== undefined) {
			return `${min.toLocaleString()} - ${max.toLocaleString()}`;
		}
		if (min !== undefined) {
			return `≥ ${min.toLocaleString()}`;
		}
		if (max !== undefined) {
			return `≤ ${max.toLocaleString()}`;
		}
		return "";
	};

	const hasValue = value?.min !== undefined || value?.max !== undefined;

	// Handle apply button click
	const handleApply = () => {
		// Convert to numbers, but guard against NaN (e.g., when input is just "-")
		const minVal =
			localMin !== "" && localMin !== "-" && !isNaN(Number(localMin))
				? Number(localMin)
				: undefined;
		const maxVal =
			localMax !== "" && localMax !== "-" && !isNaN(Number(localMax))
				? Number(localMax)
				: undefined;

		// Only set value if at least one bound is defined and valid
		if (minVal !== undefined || maxVal !== undefined) {
			onChange({ min: minVal, max: maxVal });
		} else {
			onChange(undefined);
		}
		setShow(false);
	};

	// Handle clear/cancel button click
	const handleClear = (e?: React.MouseEvent) => {
		e?.stopPropagation();
		setLocalMin("");
		setLocalMax("");
		onChange(undefined);
		setShow(false);
	};

	// Handle input changes with validation
	const handleMinChange = (e: React.ChangeEvent<HTMLInputElement>) => {
		const val = e.target.value;
		// Allow empty, numbers, and negative sign
		if (val === "" || val === "-" || !isNaN(Number(val))) {
			setLocalMin(val);
		}
	};

	const handleMaxChange = (e: React.ChangeEvent<HTMLInputElement>) => {
		const val = e.target.value;
		if (val === "" || val === "-" || !isNaN(Number(val))) {
			setLocalMax(val);
		}
	};

	// Handle Enter key to apply
	const handleKeyDown = (e: React.KeyboardEvent) => {
		if (e.key === "Enter") {
			handleApply();
		}
		if (e.key === "Escape") {
			setShow(false);
		}
	};

	return (
		<div className="numberRangeFilter" ref={containerRef}>
			<div
				className="numberRangeFilter-inputContainer"
				onClick={() => setShow(!show)}
			>
				<input
					readOnly
					placeholder={placeholder}
					value={getDisplayValue()}
					className={`numberRangeFilter-input ${hasValue ? "hasValue" : ""}`}
				/>
				{hasValue && (
					<span
						className="numberRangeFilter-clearIcon"
						onClick={handleClear}
					>
						×
					</span>
				)}
			</div>

			{show && (
				<div className="numberRangeFilter-dropdown">
					<div className="numberRangeFilter-header">
						{placeholder}
					</div>
					<div className="numberRangeFilter-inputs">
						<div className="numberRangeFilter-field">
							<label className="numberRangeFilter-label">Min</label>
							<input
								type="number"
								className="numberRangeFilter-numberInput"
								value={localMin}
								onChange={handleMinChange}
								onKeyDown={handleKeyDown}
								placeholder="No min"
								min={minAllowed}
								max={maxAllowed}
								step={step}
								autoFocus
							/>
						</div>
						<div className="numberRangeFilter-separator">–</div>
						<div className="numberRangeFilter-field">
							<label className="numberRangeFilter-label">Max</label>
							<input
								type="number"
								className="numberRangeFilter-numberInput"
								value={localMax}
								onChange={handleMaxChange}
								onKeyDown={handleKeyDown}
								placeholder="No max"
								min={minAllowed}
								max={maxAllowed}
								step={step}
							/>
						</div>
					</div>
					<div className="numberRangeFilter-actions">
						<button
							type="button"
							className="numberRangeFilter-btn numberRangeFilter-btn--clear"
							onClick={handleClear}
						>
							Clear
						</button>
						<button
							type="button"
							className="numberRangeFilter-btn numberRangeFilter-btn--apply"
							onClick={handleApply}
						>
							Apply
						</button>
					</div>
				</div>
			)}
		</div>
	);
}

export default NumberRangeFilter;

